import { cloneDeep, get, sortBy } from 'lodash';
import { IBookingStateEvent } from '../../../../../../data/entities/booking-state';
import { IEventV2, IEventV3 } from '../../../../../../data/entities/event';
import { IBookingBreakdown } from '../../../../../../data/entities/event/booking';
import { Channel } from '../../../../../../entities/contracts/constants';
import { sortByCreatedAt } from '../../../../../components/booking-list/helpers';

export interface IFXRateInfo {
	from: string;
	to: string;
	fxRate: number;
}

export interface IFXRates {
	[date: string]: IFXRateInfo;
}

export const calculateDiscrepancyRate = (
	bookingDiscrepancy: number,
	total: number
) => {
	// the discrepancy is between the bookingDiscrepancy and the value we (TN) believe the booking
	// should come to (TBV minus the discrepancy)
	return total > 0 ? bookingDiscrepancy / (total - bookingDiscrepancy) : 0;
};

export const calculateOwnerTotal = (
	ownerRent: number,
	extras: number,
	touristTax?: number
) => {
	return ownerRent + extras + (touristTax || 0);
};

const RestrictionInfo = {
	'early arrival': 'Early arrival',
	smoking: 'Smoking',
	pets: 'Pets',
	wheelchair: 'Wheelchair',
	'group bookings': 'Group bookings',
	'children allowed': 'Children allowed',
	'infants allowed': 'Infants allowed',
	'late departure': 'Late departure',
	'extra occupancy': 'Extra occupancy',
	'cleaning fee': 'Cleaning fee',
	'tourist tax': 'Tourist tax',
};

export const zeroIsTruthy = (value: number) => {
	return !!value || value === 0;
};

export const displayVersionOfRestictionName = (restriction: string) =>
	RestrictionInfo[restriction] || 'Extra fee';

export const financesHaveDiscrepancy = (
	breakdown: IBookingBreakdown,
	total: number | undefined
) => {
	const safeTotal = total || 0;
	const acceptableDiscrepancyPercent = 10;
	const discrepancyPercent =
		calculateDiscrepancyRate(
			get(breakdown, 'travelnest.booking_discrepancy'),
			safeTotal
		) * 100;
	return Math.abs(discrepancyPercent) > acceptableDiscrepancyPercent;
};

export const breakdownHasSucceeded = (breakdown: IBookingBreakdown) => {
	return (
		zeroIsTruthy(get(breakdown, 'travelnest.commission_rate')) &&
		zeroIsTruthy(get(breakdown, 'owner.rent_value'))
	);
};

export const calculateCSBookingFee = (
	total: number,
	discountedOwnerRent: number,
	extras: number[]
) => {
	if (typeof total !== 'number') return;
	if (typeof discountedOwnerRent !== 'number') return;
	const totalExtras = extras.reduce((sum, curr) => sum + curr, 0);
	return total - (totalExtras + discountedOwnerRent);
};

export const isFirstBooking = (bookings: IEventV3[], bookingId: string) => {
	const first =
		bookings &&
		cloneDeep(bookings)
			.filter((b) => b.status !== 'failed')
			.sort(sortByCreatedAt(true))[0];
	return get(first, 'id') === bookingId;
};

export const VCC_STATUS_NOTE_TYPE = 'vcc status';

/**
 * Look for a VCC status note in a given event to determine if it is a vcc booking.
 * @param booking IEventV2 - The event to check
 *
 * @returns isVCC {boolean} - True if there is a note indicating the booking is a VCC booking
 */
export const isVCCBooking = (booking: IEventV2): boolean => {
	// Sort latest created descending to get most recent VCC note
	const vccStatusNote = booking.notes
		.sort((a, b) => (b.created_at < a.created_at ? -1 : 1))
		.find((n) => n.note_type === 'vcc status');
	if (vccStatusNote) {
		return vccStatusNote.content.startsWith('true');
	} else {
		return false;
	}
};

export const isBookingConfirmed = (
	event: IEventV3 | IEventV2,
	stateEvents: IBookingStateEvent[]
): boolean => {
	const { notes } = event;

	const stateResult =
		!!stateEvents &&
		stateEvents.some(
			(state) =>
				(state.state_event_type &&
					state.state_event_type.includes(
						'BOOKING_MANUALLY_CONFIRMED'
					)) ||
				state.state_event_type.includes('VERIFICATION_PASSED')
		);
	const notesResult =
		!!notes &&
		notes.some(
			(note) =>
				note.content.includes('manual_booking_confirmation') ||
				note.content.includes('owner_booking_confirmation')
		);
	return stateResult || notesResult;
};

export const getCoreChannel = (source: string): string => {
	const result = source.split(':');
	return result[0] === Channel.RENTALS_UNITED ? result[1] : result[0];
};

export const sortArraysInBookingObject = (obj: { [key: string]: any }) => {
	if (Array.isArray(obj)) {
		// Sort the array and recursively sort its elements
		return sortBy(obj, ['created_at', 'id']);
	} else if (obj !== null && typeof obj === 'object') {
		// Recursively sort each property of the object
		const sortedObject: { [key: string]: any } = {};
		for (const key of Object.keys(obj)) {
			sortedObject[key] = sortArraysInBookingObject(obj[key]);
		}
		return sortedObject;
	} else {
		// Return the value if it's neither an array nor an object
		return obj;
	}
};

export type TaxSubType =
	| 'ABSORBED_IN_RENT'
	| 'COLLECTED_ON_TOP_OF_RENT'
	| 'WITHHELD_BY_CHANNEL'
	| 'COLLECT_ON_SITE'
	| 'NO_TAX';

export const getTouristTaxType = (
	event: IEventV3 | IEventV2
): TaxSubType | null => {
	if (!event.booking) {
		return null;
	}
	if ('booking_value' in event.booking) {
		return event.booking.booking_value.tourist_tax_type as TaxSubType;
	} else if ('value' in event.booking) {
		return event.booking.value.breakdown.owner
			?.tourist_tax_type as TaxSubType;
	}
	return null;
};

export const touristTaxAlertContent = {
	WITHHELD_BY_CHANNEL: {
		HEADER: 'withheld by channel',
		CONTENT_ADMIN:
			"Tourist Tax for this booking has been collected by Booking.com and will be passed to the authorities on the host's behalf.",
		CONTENT_HOST:
			'Tourist Tax for this booking has been collected by Booking.com and will be passed to the authorities on your behalf.',
	},
	COLLECTED_ON_TOP_OF_RENT: {
		HEADER: 'collected on top of rent',
		CONTENT_ADMIN:
			'Tourist Tax for this booking has been collected by Booking.com and will be paid to the host.',
		CONTENT_HOST:
			'Tourist Tax for this booking has been collected by Booking.com and will be paid to you.',
	},
	ABSORBED_IN_RENT: {
		HEADER: 'absorbed in rent',
		CONTENT_ADMIN:
			'Tourist Tax for this booking is included in the rental value.',
		CONTENT_HOST:
			'Tourist Tax for this booking is included in the rental value.',
	},
	COLLECT_ON_SITE: {
		HEADER: 'collected on site',
		CONTENT_ADMIN: (taxValue: string) =>
			`Tourist Tax for this booking has not been collected. The host should collect ${taxValue} directly from the guest.`,
		CONTENT_HOST: (taxValue: string) =>
			`Tourist Tax for this booking has not been collected. Please collect ${taxValue} directly from the guest.`,
	},
	NO_TAX: {
		HEADER: 'no tax',
		CONTENT_ADMIN: 'This booking has not incurred any Tourist Tax.',
		CONTENT_HOST: 'This booking has not incurred any Tourist Tax.',
	},
};

export const getTouristTaxNoticeContent = (
	content: string | ((taxValue: string) => string),
	value: string
) => (typeof content === 'function' ? content(value) : content);

// Helper function to combine booking date with property check-in time
export const getArrivalDateTime = (startDate: string, checkInTime: string): string => {
	if (!checkInTime) {
		// Return date with midnight time (earliest possible check-in)
		const arrivalDate = new Date(startDate);
		arrivalDate.setHours(0, 0, 0, 0);
		return arrivalDate.toISOString();
	}

	// Parse the start date (YYYY-MM-DD format)
	const date = new Date(startDate);

	// Parse check-in time (assuming format like "15:00" or "3:00 PM")
	const timeParts = checkInTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
	if (timeParts) {
		let hours = parseInt(timeParts[1], 10);
		const minutes = parseInt(timeParts[2], 10);
		const meridiem = timeParts[3];

		// Convert to 24-hour format if needed
		if (meridiem) {
			if (meridiem.toUpperCase() === 'PM' && hours !== 12) {
				hours += 12;
			} else if (meridiem.toUpperCase() === 'AM' && hours === 12) {
				hours = 0;
			}
		}

		date.setHours(hours, minutes, 0, 0);
	}

	return date.toISOString();
};

export const calculateMaxDeferralHours = (
	startOn: string,
	checkInTime: string,
	cancellationDate: string
): number => {
	const arrivalDateTime = getArrivalDateTime(startOn, checkInTime);
	const arrival = new Date(arrivalDateTime);
	const currentCancellation = new Date(cancellationDate);

	// Calculate 48 hours before arrival
	const fortyEightHoursBeforeArrival = new Date(
		arrival.getTime() - 48 * 60 * 60 * 1000
	);

	// Calculate hours between current cancellation date and 48 hours before arrival
	const hoursDifference = Math.floor(
		(fortyEightHoursBeforeArrival.getTime() -
			currentCancellation.getTime()) /
			(1000 * 60 * 60)
	);

	// Return the maximum hours we can defer (minimum of 0)
	const maxAllowedHours = Math.max(0, hoursDifference);

	return maxAllowedHours;
};
